/**
 * Schema.org builders for the site's structured data.
 *
 * Everything here is derived from Sanity or from constants.ts — never
 * hardcoded separately — so the markup cannot drift from what the pages
 * actually say. Structured data that contradicts the visible page is a
 * manual-action risk, not just a wasted opportunity.
 */
import {
	BASE_URL,
	COMPANY_EMAIL,
	COMPANY_NAME,
	COMPANY_PHONE,
} from '#app/utils/constants.ts'

/** Full state names that may come from the CMS, mapped to their postal codes. */
const US_STATE_CODES: Record<string, string> = {
	florida: 'FL',
}

/** Stable node ids so other schemas can reference the business by @id. */
export const BUSINESS_ID = `${BASE_URL}/#business`
export const WEBSITE_ID = `${BASE_URL}/#website`

type Address = {
	streetAddress?: string | null
	city?: string | null
	state?: string | null
	zipCode?: string | null
	googleMapsLink?: string | null
} | null

type BusinessInput = {
	companyName?: string | null
	phoneNumber?: string | null
	emailAddress?: string | null
	isOpen247?: boolean | null
	physicalAddress?: Address
	logoUrl?: string | null
	socialLinks?: Array<string>
	/** County names, e.g. "Seminole County". */
	areaServed?: Array<string>
}

/**
 * The primary entity for the site. `HomeAndConstructionBusiness` is the
 * closest schema.org type for a garage door contractor and is a LocalBusiness
 * subtype, so it keeps the local-pack signals.
 */
export function localBusinessSchema(input: BusinessInput) {
	const addr = input.physicalAddress
	// Trailing comma in the CMS value ("508 Central Park Drive,") would end up
	// in the markup Google reads.
	const street = addr?.streetAddress?.trim().replace(/,+$/, '')
	// schema.org addressRegion wants the state code ("FL"), not "Florida".
	const region = addr?.state?.trim()
	const addressRegion =
		region && US_STATE_CODES[region.toLowerCase()]
			? US_STATE_CODES[region.toLowerCase()]
			: region

	return {
		'@context': 'https://schema.org',
		'@type': 'HomeAndConstructionBusiness',
		'@id': BUSINESS_ID,
		name: input.companyName || COMPANY_NAME,
		url: BASE_URL,
		telephone: input.phoneNumber || COMPANY_PHONE,
		email: input.emailAddress || COMPANY_EMAIL,
		...(input.logoUrl ? { logo: input.logoUrl, image: input.logoUrl } : {}),
		...(street && addr
			? {
					address: {
						'@type': 'PostalAddress',
						streetAddress: street,
						addressLocality: addr.city ?? undefined,
						addressRegion: addressRegion ?? undefined,
						postalCode: addr.zipCode ?? undefined,
						addressCountry: 'US',
					},
				}
			: {}),
		...(addr?.googleMapsLink ? { hasMap: addr.googleMapsLink } : {}),
		...(input.isOpen247
			? {
					openingHoursSpecification: {
						'@type': 'OpeningHoursSpecification',
						dayOfWeek: [
							'Monday',
							'Tuesday',
							'Wednesday',
							'Thursday',
							'Friday',
							'Saturday',
							'Sunday',
						],
						opens: '00:00',
						closes: '23:59',
					},
				}
			: {}),
		...(input.areaServed?.length
			? {
					areaServed: input.areaServed.map((county) => ({
						'@type': 'AdministrativeArea',
						name: county,
					})),
				}
			: {}),
		...(input.socialLinks?.length ? { sameAs: input.socialLinks } : {}),
	}
}

export function webSiteSchema(name?: string | null) {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		'@id': WEBSITE_ID,
		url: BASE_URL,
		name: name || COMPANY_NAME,
		publisher: { '@id': BUSINESS_ID },
	}
}

/** A single service offering, provided by the business node. */
export function serviceSchema(input: {
	name: string
	description?: string | null
	url: string
	areaServed?: Array<string>
	/** Cities are City; counties are AdministrativeArea. */
	areaType?: 'City' | 'AdministrativeArea'
}) {
	return {
		'@context': 'https://schema.org',
		'@type': 'Service',
		name: input.name,
		...(input.description ? { description: input.description } : {}),
		url: input.url,
		serviceType: input.name,
		provider: { '@id': BUSINESS_ID },
		...(input.areaServed?.length
			? {
					areaServed: input.areaServed.map((area) => ({
						'@type': input.areaType ?? 'AdministrativeArea',
						name: area,
					})),
				}
			: {}),
	}
}

/**
 * FAQPage. Only emit this where the questions and answers are actually
 * visible on the page — Google treats hidden FAQ markup as a violation.
 */
export function faqPageSchema(
	faqs: Array<{ question?: string | null; answer?: string | null }>,
) {
	const entities = faqs
		.filter((f) => f.question && f.answer)
		.map((f) => ({
			'@type': 'Question',
			name: f.question,
			acceptedAnswer: { '@type': 'Answer', text: f.answer },
		}))

	if (!entities.length) return null

	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: entities,
	}
}

export function breadcrumbSchema(items: Array<{ name: string; path: string }>) {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: items.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			item: `${BASE_URL}${item.path}`,
		})),
	}
}

/**
 * Flattens Sanity portable text to the plain string FAQPage expects.
 * Non-text blocks (images, embeds) are skipped rather than rendered as noise.
 */
export function portableTextToPlain(blocks: unknown): string {
	if (typeof blocks === 'string') return blocks
	if (!Array.isArray(blocks)) return ''

	return blocks
		.map((block: any) => {
			if (!block || block._type !== 'block' || !Array.isArray(block.children)) {
				return ''
			}
			return block.children
				.map((child: any) => (typeof child?.text === 'string' ? child.text : ''))
				.join('')
		})
		.filter(Boolean)
		.join(' ')
		.replace(/\s+/g, ' ')
		.trim()
}

/** Pulls every FAQ out of a page's CMS sections, whatever section type holds them. */
export function faqsFromSections(sections: unknown): Array<{
	question: string
	answer: string
}> {
	if (!Array.isArray(sections)) return []

	return sections
		.flatMap((section: any) => (Array.isArray(section?.faqs) ? section.faqs : []))
		.map((faq: any) => ({
			question: typeof faq?.question === 'string' ? faq.question : '',
			answer: portableTextToPlain(faq?.answer),
		}))
		.filter((faq) => faq.question && faq.answer)
}
